import { useQuery } from '@tanstack/react-query';
import knowledgeDashboardApi from '../api/knowledge-dashboard.service.api';
import { queryKeys } from '../api/query-client-registry';

/**
 * React-query hooks for the dashboard widgets.
 *
 * @typedef {import('../api/knowledge-dashboard.service.api').KnowledgeItem} KnowledgeItem
 * @typedef {import('../api/knowledge-dashboard.service.api').KnowledgeItemRef} KnowledgeItemRef
 * @typedef {import('../api/knowledge-dashboard.service.api').QualityMetric} QualityMetric
 */

/** Secondary content — the `useEffect` code this replaced retried zero times. */
const RETRY = 1;

/** `all` lives in the registry — the knowledge middleware invalidates this domain. */
export const knowledgeDashboardKeys = {
  all: queryKeys.knowledgeDashboard,
  openEventsForOrganization: () => [...knowledgeDashboardKeys.all, 'openEvents', 'organization'],
  openEventsForUser: () => [...knowledgeDashboardKeys.all, 'openEvents', 'user'],
  recentlyCreated: workspaceId => [...knowledgeDashboardKeys.all, 'recentlyCreated', workspaceId],
  recentlyUpdated: workspaceId => [...knowledgeDashboardKeys.all, 'recentlyUpdated', workspaceId],
  myQualityMetrics: () => [...knowledgeDashboardKeys.all, 'qualityMetrics', 'me'],
};

/**
 * @param {import('../api/query-client').QueryOpts<KnowledgeItemRef[]>} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<KnowledgeItemRef[]>}
 */
export function useItemsOpenEventsForOrganization(options = {}) {
  return useQuery({
    queryKey: knowledgeDashboardKeys.openEventsForOrganization(),
    queryFn: ({ signal }) => knowledgeDashboardApi.getItemsOpenEventsForOrganization({ signal }),
    retry: RETRY,
    ...options,
  });
}

/**
 * @param {import('../api/query-client').QueryOpts<KnowledgeItemRef[]>} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<KnowledgeItemRef[]>}
 */
export function useItemsOpenEventsForUser(options = {}) {
  return useQuery({
    queryKey: knowledgeDashboardKeys.openEventsForUser(),
    queryFn: ({ signal }) => knowledgeDashboardApi.getItemsOpenEventsForUser({ signal }),
    retry: RETRY,
    ...options,
  });
}

/**
 * @param {string} [workspaceId] falsy disables the query
 * @param {import('../api/query-client').QueryOpts<KnowledgeItem[]>} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<KnowledgeItem[]>}
 */
export function useRecentlyCreatedItems(workspaceId, options = {}) {
  return useQuery({
    queryKey: knowledgeDashboardKeys.recentlyCreated(workspaceId),
    queryFn: ({ signal }) => knowledgeDashboardApi.getRecentlyCreatedItems({ workspaceId, signal }),
    retry: RETRY,
    ...options,
    // After the spread, so a caller's `enabled` can only narrow this.
    enabled: !!workspaceId && (options.enabled ?? true),
  });
}

/**
 * @param {string} [workspaceId] falsy disables the query
 * @param {import('../api/query-client').QueryOpts<KnowledgeItem[]>} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<KnowledgeItem[]>}
 */
export function useRecentlyUpdatedItems(workspaceId, options = {}) {
  return useQuery({
    queryKey: knowledgeDashboardKeys.recentlyUpdated(workspaceId),
    queryFn: ({ signal }) => knowledgeDashboardApi.getRecentlyUpdatedItems({ workspaceId, signal }),
    retry: RETRY,
    ...options,
    enabled: !!workspaceId && (options.enabled ?? true),
  });
}

/**
 * @param {import('../api/query-client').QueryOpts<QualityMetric[]>} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<QualityMetric[]>}
 */
export function useMyQualityMetrics(options = {}) {
  return useQuery({
    queryKey: knowledgeDashboardKeys.myQualityMetrics(),
    queryFn: ({ signal }) => knowledgeDashboardApi.getMyQualityMetrics({ signal }),
    retry: RETRY,
    ...options,
  });
}
